import { useEffect, useMemo } from 'react';
import { getErrorMessage } from '@/utils';
import useRoutes from '../hooks/useRoutes';
import { ROUTES_TABLE_CARD_CLASS } from '../constants/routesTable.constants';
import { RoutesFilter } from '../models';
import RoutesTable from './RoutesTable';
import { useGetCommerceByIdQuery } from '../../Commerce/services/commerces.service';

const DEFAULT_ROUTES_LOAD_ERROR =
  'Ocurrió un error al cargar las rutas. Por favor, intente nuevamente.';

interface RouteListProps {
  filter: RoutesFilter;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const RouteList = ({
  filter,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: RouteListProps) => {
  const commerceIdForTable = filter.commerceId?.trim() ?? '';
  const { data: commerceForTable } = useGetCommerceByIdQuery(
    { commerceId: commerceIdForTable },
    { skip: !commerceIdForTable }
  );

  const commerceTableFilter = useMemo(
    () =>
      commerceIdForTable
        ? {
            commerceName: commerceForTable?.name?.trim() ?? '',
          }
        : undefined,
    [commerceIdForTable, commerceForTable?.name]
  );

  const syncVisitSearchFromRouteShippingNumber =
    filter.shippingNumber?.trim() || undefined;

  const { routes, totalItems, totalPages, isLoading, error } = useRoutes({
    filters: filter,
    page: currentPage,
    pageSize,
  });

  useEffect(() => {
    if (!error) {
      return;
    }
    console.error(
      'Error al cargar rutas:',
      getErrorMessage(error, DEFAULT_ROUTES_LOAD_ERROR)
    );
  }, [error]);

  if (isLoading) {
    return (
      <RoutesTable
        routes={[]}
        isLoading
        totalItems={0}
        totalPages={0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        commerceTableFilter={commerceTableFilter}
        syncVisitSearchFromRouteShippingNumber={
          syncVisitSearchFromRouteShippingNumber
        }
      />
    );
  }

  if (error) {
    return (
      <div className={ROUTES_TABLE_CARD_CLASS}>
        <div className='text-center text-red-600 py-8'>
          {getErrorMessage(error, DEFAULT_ROUTES_LOAD_ERROR)}
        </div>
      </div>
    );
  }

  if (!routes || routes.length === 0) {
    return (
      <div className={ROUTES_TABLE_CARD_CLASS}>
        <div className='text-center text-gray-500 py-8'>
          No hay rutas por el momento
        </div>
      </div>
    );
  }

  return (
    <RoutesTable
      routes={routes}
      isLoading={false}
      totalItems={totalItems}
      totalPages={totalPages}
      currentPage={currentPage}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      commerceTableFilter={commerceTableFilter}
      syncVisitSearchFromRouteShippingNumber={
        syncVisitSearchFromRouteShippingNumber
      }
    />
  );
};

export default RouteList;
